import React from "react";
import { ReportContainer } from "../template/constant";
import { Address } from "../constants/ProviderGroupFormInitialValues";
import {
  getAddressFormat,
  // getIdFromObj, // Invoice Report update - used only by removed Provider ID line
  // getTrimmedUuid, // Invoice Report update - used only by removed Provider ID line
  // getValueFromObj, // Invoice Report update - used only by removed CPT table
  replaceUnderScoreAddSpace,
} from "../utils/utils";
import { getFormattedDateWithoutTmz } from "../utils/date-time-utility";

// Raw form data interface (from formik values)
interface SuperBillFormData {
  ecarehealthPatientId?: string;
  patientId?: Record<string, string>;
  patientDOB?: string;
  patientPhone?: string;
  patientAddress?: Address;
  dateGenerated?: string;
  uuid?: Record<string, string>;
  locationId?: Record<string, string>;
  placeOfService?: string;
  dateOfService?: string | null;
  previousBalance?: string;
  serviceState?: string;
  renderingproviderId?: Record<string, string>;
  renderingProviderNpi?: string;
  renderingProviderLicenseNumber?: string;
  priorAuthorization?: string;
  billingProviderId?: Record<string, string>;
  referringProviderId?: Record<string, string>;
  orderingProviderId?: Record<string, string>;
  diagnisisCodeData?: Array<{
    uuid: string;
    codeVersion: string;
    type: string;
    code: string;
    description: string;
    source: string;
    active: boolean;
    archive: boolean;
    errorMessage: string;
  }>;
  diagnosisCodes?: Array<{
    uuid: string;
    codeVersion: string;
    type: string;
    code: string;
    description: string;
    source: string;
    active: boolean;
    archive: boolean;
    errorMessage: string;
  }>;
  subTotal?: string;
  advancedPaid?: string;
  insuranceAmount?: string;
  total?: string;
  prevPaid?: string;
  patientBalance?: string;
}

interface SuperBillReportProps {
  providerGroupData: any;
  formData: SuperBillFormData;
  procedureCodes?: any;
}

const SuperBillReport: React.FC<SuperBillReportProps> = ({
  providerGroupData,
  formData,
  // procedureCodes, // Invoice Report update - used only by removed CPT table
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Helper function to get first value from record
  const getFirstValue = (
    record: Record<string, string> | undefined | null
  ): string => {
    if (!record || typeof record !== "object") return "";
    const values = Object.values(record);
    return values.length > 0 ? values[0] : "";
  };

  // Helper function to format date
  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return "";
    return getFormattedDateWithoutTmz(dateString) ?? "";
  };

  // Helper function to get place of service description
  const getPlaceOfServiceDescription = (
    posCode: string | undefined | null
  ): string => {
    if (!posCode) return "";
    const posDescriptions: Record<string, string> = {
      TELEHEALTH_INSIDE_PATIENT_HOME:
        "10 - Telehealth Provided in Patient's Home",
      OFFICE: "11 - Office",
      INPATIENT_HOSPITAL: "21 - Inpatient Hospital",
      OUTPATIENT_HOSPITAL: "22 - Outpatient Hospital",
      EMERGENCY_ROOM: "23 - Emergency Room",
      AMBULATORY_SURGICAL_CENTER: "24 - Ambulatory Surgical Center",
      BIRTHING_CENTER: "25 - Birthing Center",
      MILITARY_TREATMENT_FACILITY: "26 - Military Treatment Facility",
    };
    return posDescriptions[posCode] || posCode;
  };

  // Helper function to get state name from code
  const getStateName = (stateCode: string | undefined | null): string => {
    if (!stateCode) return "";
    const stateNames: Record<string, string> = {
      "1": "Alabama",
      "2": "Alaska",
      "3": "Arizona",
      "4": "Arkansas",
      "5": "California",
      "6": "Colorado",
      "7": "Connecticut",
      "8": "Delaware",
      "9": "Florida",
      "10": "Georgia",
      // Add more states as needed
    };
    return stateNames[stateCode] || stateCode;
  };

  interface SuperBillContainerProps {
    label: string;
    children: React.ReactNode;
  }

  const SuperBillContainer = ({ label, children }: SuperBillContainerProps) => {
    return (
      <div style={{ marginTop: "10px" }}>
        <ReportContainer label={label} color={"#F2F6F8"}>
          {children}
        </ReportContainer>
      </div>
    );
  };

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        maxWidth: "800px",
        margin: "0 auto",
        backgroundColor: "white",
        color: "#333",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "#1e40af",
          color: "white",
          padding: "20px",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <h1
              style={{
                margin: "0 0 10px 0",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              {providerGroupData?.name}
            </h1>
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              Address:
              {getAddressFormat(
                providerGroupData?.billingAddress ||
                  providerGroupData?.physicalAddress
              ) ?? "-"}
            </p>
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              Phone: {providerGroupData?.phone} | Fax: {providerGroupData?.fax}
            </p>
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              Email: {providerGroupData?.email}
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            {/* <p style={{ margin: "2px 0", fontSize: "14px" }}> */}
            {/* todo: need to add from B.E side */}
            {/* Tax ID: {providerGroupData?.taxId}
            </p> */}
            {/* Invoice Report update - remove Provider NPI */}
            {/* <p style={{ margin: "2px 0", fontSize: "14px" }}>
              NPI: {providerGroupData?.npi}
            </p> */}
            {/* <p style={{ margin: "2px 0", fontSize: "14px" }}>
              Provider ID:{" "}
              {getTrimmedUuid(
                formData?.renderingproviderId
                  ? (getIdFromObj(formData?.renderingproviderId) ?? "")
                  : "-"
              ) ?? "-"}
            </p> */}
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              License: {formData?.renderingProviderLicenseNumber ?? "-"}
            </p>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: "15px" }}>
          <h2 style={{ margin: "0", fontSize: "20px", fontWeight: "bold" }}>
            INVOICE REPORT
          </h2>
          {/* <p style={{ margin: "5px 0 0 0", fontSize: "14px", opacity: 0.9 }}>
            Healthcare Services Documentation
          </p> */}
        </div>
      </div>

      {/* Patient Information */}
      <SuperBillContainer label="Patient Information">
        <div style={{ paddingLeft: "14px", display: "flex", gap: "15px" }}>
          <div style={{ width: "50%" }}>
            <p style={{ margin: "5px 0" }}>
              <strong>Patient Name:</strong>{" "}
              {formData.uuid ? getFirstValue(formData.uuid) : "-"}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Patient ID:</strong>{" "}
              {formData.ecarehealthPatientId || "-"}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Date of Birth:</strong>{" "}
              {formatDate(formData?.patientDOB) || "-"}
            </p>
          </div>
          <div style={{ width: "50%" }}>
            <p style={{ margin: "5px 0" }}>
              <strong>Phone:</strong> {formData?.patientPhone || "-"}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Address:</strong>{" "}
              {getAddressFormat(formData?.patientAddress) || "-"}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Date Generated:</strong>{" "}
              {formatDate(formData?.dateGenerated) || "-"}
            </p>
          </div>
        </div>
      </SuperBillContainer>

      {/* Service Details */}
      <SuperBillContainer label="Service Details">
        <div style={{ paddingLeft: "14px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
            }}
          >
            <div>
              <p style={{ margin: "5px 0" }}>
                <strong>Service Location:</strong>{" "}
                {getFirstValue(formData.locationId) || "Not Selected"}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Place of Service:</strong>{" "}
                {getPlaceOfServiceDescription(
                  replaceUnderScoreAddSpace(formData.placeOfService ?? "")
                ) || "Not Selected"}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Date of Service:</strong>{" "}
                {formatDate(formData.dateOfService) || "Not Selected"}
              </p>
            </div>
            <div>
              <p style={{ margin: "5px 0" }}>
                <strong>Service State:</strong>{" "}
                {getStateName(formData.serviceState) || "Not Selected"}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Rendering Provider:</strong>{" "}
                {getFirstValue(formData.renderingproviderId) || "-"}
              </p>
              {/* Invoice Report update - remove Prior Authorization */}
              {/* <p style={{ margin: "5px 0" }}>
                <strong>Prior Authorization:</strong>{" "}
                {formData.priorAuthorization || "-"}
              </p> */}
            </div>
          </div>
        </div>
      </SuperBillContainer>

      {/* Invoice Report update - remove Rendering Provider Details section */}
      {/* <SuperBillContainer label="Rendering Provider Details">
        <div style={{ paddingLeft: "14px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: "15px",
            }}
          >
            <p style={{ margin: "5px 0" }}>
              <strong>Provider Name:</strong>{" "}
              {getFirstValue(formData.renderingproviderId) || "-"}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>NPI:</strong> {formData.renderingProviderNpi || "-"}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Provider ID:</strong>{" "}
              {formData.renderingproviderId
                ? (getTrimmedUuid(
                    getIdFromObj(formData.renderingproviderId) ?? ""
                  ) ?? "-")
                : "-"}
            </p>
          </div>
        </div>
      </SuperBillContainer> */}

      {/* Invoice Report update - remove Provider Details section */}
      {/* <SuperBillContainer label="Provider Details">
        <div style={{ paddingLeft: "14px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "15px",
            }}
          >
            <p style={{ margin: "5px 0" }}>
              <strong>Billing Provider:</strong>{" "}
              {getFirstValue(formData.billingProviderId) || "-"}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Referring Provider:</strong>{" "}
              {getFirstValue(formData.referringProviderId) || "-"}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Ordering Provider:</strong>{" "}
              {getFirstValue(formData.orderingProviderId) || "-"}
            </p>
          </div>
        </div>
      </SuperBillContainer> */}

      {/* Invoice Report update - remove ICD (Diagnosis) Codes */}
      {/* {formData.diagnosisCodes && formData.diagnosisCodes.length > 0 && (
        <SuperBillContainer label="Diagnosis Codes">
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid #ddd",
              padding: "10px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <TableHead>Pointer</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Description</TableHead>
              </tr>
            </thead>
            <tbody>
              {formData.diagnosisCodes.map((diagnosis, index) => (
                <tr key={index}>
                  <TableCell>{String.fromCharCode(65 + index)}</TableCell>
                  <TableCell>{diagnosis.code}</TableCell>
                  <TableCell>{diagnosis.description}</TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </SuperBillContainer>
      )} */}

      {/* Invoice Report update - remove CPT (Procedure) Codes */}
      {/* {procedureCodes?.length > 0 && (
        <SuperBillContainer label="Procedure Codes">
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid #ddd",
              padding: "10px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <TableHead>Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Pointer</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Charges</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Total</TableHead>
              </tr>
            </thead>
            <tbody>
              {procedureCodes?.map((procedure, index: number) => (
                <tr key={index}>
                  <TableCell>
                    {getValueFromObj(procedure?.procedureCode)?.split("-")?.[0]}
                  </TableCell>
                  <TableCell>
                    {getValueFromObj(procedure?.procedureCode)?.split("-")?.[1]}
                  </TableCell>
                  <TableCell>
                    {POINTERS.map((pointer) => procedure[pointer]).join("  ")}
                  </TableCell>
                  <TableCell>{procedure.quantity}</TableCell>
                  <TableCell>{formatCurrency(procedure.charges)}</TableCell>
                  <TableCell>{`${procedure.discount ?? 0}%`}</TableCell>
                  <TableCell>{formatCurrency(procedure.total)}</TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </SuperBillContainer>
      )} */}

      {/* Patient Balance */}
      <SuperBillContainer label="Service Charges">
        <div style={{ paddingLeft: "14px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: "15px",
            }}
          >
            <div>
              <p style={{ margin: "5px 0", fontSize: "14px" }}>
                <strong>Subtotal:</strong>
              </p>
              <p style={{ margin: "0", fontSize: "16px", fontWeight: "bold" }}>
                {formatCurrency(Number(formData.subTotal) || 0)}
              </p>
            </div>
            <div>
              <p style={{ margin: "5px 0", fontSize: "14px" }}>
                <strong>Paid Amount:</strong>
              </p>
              <p style={{ margin: "0", fontSize: "16px", fontWeight: "bold" }}>
                {formatCurrency(Number(formData.prevPaid) || 0)}
              </p>
            </div>
            {/* Invoice Report update - remove Advance Paid */}
            {/* <div>
              <p style={{ margin: "5px 0", fontSize: "14px" }}>
                <strong>Advance Paid:</strong>
              </p>
              <p
                style={{
                  margin: "0",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#dc2626",
                }}
              >
                -{formatCurrency(Number(formData.advancedPaid) || 0)}
              </p>
            </div> */}
            {/* eCarehealth Production -10th july Tracker - Remove below fields Previous balance >  Ins amt  */}
            {/* <div>
              <p style={{ margin: "5px 0", fontSize: "14px" }}>
                <strong>Insurance Amount:</strong>
              </p>
              <p
                style={{
                  margin: "0",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#dc2626",
                }}
              >
                -{formatCurrency(Number(formData.insuranceAmount) || 0)}
              </p>
            </div> */}
            {/* <div>
              <p style={{ margin: "5px 0", fontSize: "14px" }}>
                <strong>Previous Balance:</strong>
              </p>
              <p style={{ margin: "0", fontSize: "14px", fontWeight: "bold" }}>
                {formatCurrency(Number(formData.previousBalance) || 0)}
              </p>
            </div> */}
            {/* Reads patientBalance so the printed Total Balance is the same
                value the invoice listing shows in its Patient Balance column. */}
            <div>
              <p style={{ margin: "5px 0", fontSize: "14px" }}>
                <strong>Total Balance:</strong>
              </p>
              <p
                style={{
                  margin: "0",
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#1e40af",
                }}
              >
                {formatCurrency(Number(formData.patientBalance) || 0)}
              </p>
            </div>
          </div>
        </div>
      </SuperBillContainer>

      {/* Authorization */}
      {/* <p style={{ margin: "0", fontSize: "14px", fontStyle: "italic" }}>
          I authorize the release of any medical information necessary to
          process this claim.
        </p> */}

      {/* Signature Section */}
      <SuperBillContainer label="Signature">
        <div style={{ display: "flex", gap: "15px" }}>
          <div style={{ width: "48%" }}>
            <p style={{ margin: "0 0 20px 0", fontSize: "14px" }}>
              Date: _______________________
            </p>
            <p style={{ margin: "0", fontSize: "14px" }}>
              Patient Signature: _______________________
            </p>
          </div>
          {/* Invoice Report update - remove Provider Signature */}
          {/* <div style={{ width: "48%" }}>
            <p style={{ margin: "0 0 20px 0", fontSize: "14px" }}>
              Date: _______________________
            </p>
            <p style={{ margin: "0", fontSize: "14px" }}>
              Provider Signature: _______________________
            </p>
          </div> */}
        </div>
      </SuperBillContainer>
      {/* Footer
      <div
        style={{
          marginTop: "30px",
          paddingTop: "20px",
          borderTop: "1px solid #e5e7eb",
          textAlign: "center",
          fontSize: "12px",
          color: "#6b7280",
        }}
      >
        <p style={{ margin: "0" }}>
          Procedure Charges: {formatCurrency(Number(formData.subTotal) || 0)}
        </p>
      </div> */}
    </div>
  );
};

export default SuperBillReport;

// Invoice Report update - TableHead/TableCell/POINTERS used only by the removed
// ICD (Diagnosis) and CPT (Procedure) code tables.
// const TableHead = ({ children }: { children: React.ReactNode }) => {
//   return (
//     <th
//       style={{
//         padding: "8px",
//         border: "1px solid #ddd",
//         textAlign: "left",
//       }}
//     >
//       {children}
//     </th>
//   );
// };

// const TableCell = ({ children }: { children: React.ReactNode }) => {
//   return (
//     <td style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>
//       {children}
//     </td>
//   );
// };

// const POINTERS = [
//   "diagnosisFirstPointers",
//   "diagnosisFourthPointers",
//   "diagnosisSecondPointers",
//   "diagnosisThirdPointers",
// ];
